/**
 * Interfaces describing the payload for generating a schedule for a tag (scheduling group).
 * These mirror the shape consumed by POST /api/tenants/{tenantId}/teams/{teamId}/tags/{tagId}/generateschedule
 */
export interface IGenerateScheduleBreak {
    /** Duration of the break in minutes */
    duration: number;
    /** Type/category of break e.g. Paid, Unpaid */
    breakType: string;
}

export interface IGenerateScheduleSubshift {
    /** Title of the activity or task */
    title: string;
    /** HH:mm:ss (team local time) start of subshift */
    startTime: string;
    /** HH:mm:ss (team local time) end of subshift */
    endTime: string;
    /** Optional UI theme token */
    theme?: string;
    /** Classification e.g. PaidWork | UnpaidBreak */
    subshiftType?: string;
}

export interface IGenerateScheduleDayTemplate {
    id: string;
    /** Optional ETag for optimistic concurrency */
    eTag?: string;
    /** Display title for the shift (e.g. Morning) */
    title: string;
    /** HH:mm:ss start time */
    startTime: string;
    /** HH:mm:ss end time */
    endTime: string;
    /** Start time in local team time */
    startTimeLocal?: string;
    /** HH:mm:ss end time in local team time */
    endTimeLocal?: string;
    /** Number of slots for the shift (string per service contract) */
    slots: string;
    /** If true, applies every day and repeatDays is ignored */
    repeatEveryday?: boolean;
    /** Explicit weekdays when not repeating every day */
    repeatDays?: string[];
    /** Optional notes to attach to generated shifts */
    notes?: string;
    /** Theme token for coloring */
    theme?: string;
    /** Break definitions inside the shift */
    breaks?: IGenerateScheduleBreak[];
    /** Subshift/activity blocks within the shift */
    subshifts?: IGenerateScheduleSubshift[];
}

export interface IGenerateScheduleRequest {
    /** Inclusive start date (YYYY-MM-DD) */
    startDate: string;
    /** Inclusive end date (YYYY-MM-DD) */
    endDate: string;
    /** Inclusive start date (YYYY-MM-DD) in local team time */
    startDateLocal?: string;
    /** Inclusive end date (YYYY-MM-DD) in local team time */
    endDateLocal?: string;
    /** If true, existing schedule items in the range can be overridden */
    overrideExistingSchedule: boolean;
    /** Shift templates per day used to generate schedule */
    shiftsPerDay: IGenerateScheduleDayTemplate[];
}

/**
 * Basic runtime validator to help catch obvious malformed request bodies before sending.
 * Returns an error message string if invalid; undefined if valid.
 */
export function validateGenerateScheduleRequest(req: IGenerateScheduleRequest): string | undefined {
    if (!req) { return "Request is null"; }
    if (!req.startDate) { return "startDate is required"; }
    if (!req.endDate) { return "endDate is required"; }
    if (!Array.isArray(req.shiftsPerDay) || req.shiftsPerDay.length === 0) { return "At least one shift template is required"; }
    return undefined;
}
